/**
 * As contas da página do Desk Cloud (docs/spec/desk-cloud.md), sem tela: o lugar aberto, o caminho, a
 * seleção (Ctrl e Shift), as cores dos tipos e das mesas, os textos de tamanho, duração e lixeira e as
 * ações de "Usar na mesa" de cada tipo.
 */
import { DESK_CLOUD_LIMITS, folderChain, type DeskCloudSummary, type DeskFile, type DeskFolder, type DeskRoom, type FileKind, type FileView } from '../shared.js';

/** O lugar aberto: uma visão, uma mesa ou uma pasta. */
export type CloudLocation = { view: Exclude<FileView, 'room' | 'folder'> } | { view: 'room'; roomId: string } | { view: 'folder'; folderId: string };

export const HOME: CloudLocation = { view: 'all' };

export const sameLocation = (a: CloudLocation, b: CloudLocation): boolean =>
  a.view === b.view && (a.view !== 'room' || a.roomId === (b as { roomId: string }).roomId) && (a.view !== 'folder' || a.folderId === (b as { folderId: string }).folderId);

/** A cor de cada tipo (a bolinha do chip, a barra do espaço), mais as fotos e a lixeira. */
export const KIND_COLORS: Readonly<Record<FileKind | 'photo' | 'trash', string>> = {
  map: '#5bc27e',
  token: '#b3b0f5',
  audio: '#eaa45a',
  doc: '#f08a98',
  other: '#a8a8a8',
  photo: '#7cc4e8',
  trash: '#6b6b6b',
};

const ROOM_COLORS = ['#6f6420', '#2b6a73', '#6a3d7a', '#7a4a2b', '#2b4f7a', '#4a6a2b', '#7a2b45', '#3d3d7a'];

/** A cor fixa de uma mesa (as iniciais na lateral e nos cartões), do id. */
export function roomColor(id: string): string {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return ROOM_COLORS[hash % ROOM_COLORS.length]!;
}

/** As iniciais de uma mesa: a primeira letra das duas primeiras palavras ("Macros e Testes" → "MT"). */
export function roomInitials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 2 || /^\p{Lu}/u.test(word));
  const picked = (words.length ? words : [name.trim()]).slice(0, 2);
  return picked.map((word) => [...word][0] ?? '').join('').toLocaleUpperCase() || '?';
}

/** O nome sem a extensão, e a extensão (sem o ponto, em minúsculas). */
export function splitName(name: string): { stem: string; ext: string } {
  const dot = name.lastIndexOf('.');
  if (dot <= 0 || dot === name.length - 1) return { stem: name, ext: '' };
  return { stem: name.slice(0, dot), ext: name.slice(dot + 1).toLowerCase() };
}

/** A duração do áudio: "3:12", "1:02:05". */
export function durationText(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

/** Os dias que faltam para o arquivo sair da lixeira (0 no último). */
export function trashDaysLeft(trashedAt: number, now = Date.now()): number {
  return Math.max(0, Math.ceil((trashedAt + DESK_CLOUD_LIMITS.trashMs - now) / (24 * 60 * 60 * 1000)));
}

/**
 * A seleção depois de um clique: `single` fica só com ele, `toggle` (Ctrl/Cmd, a caixa) põe ou tira,
 * `range` (Shift) pega do último clicado até ele, na ordem da tela. A âncora é o último clicado sem Shift.
 */
export function nextSelection(
  current: ReadonlySet<string>,
  id: string,
  mode: 'single' | 'toggle' | 'range',
  order: readonly string[],
  anchor: string | null,
): { selected: Set<string>; anchor: string | null } {
  if (mode === 'single') return { selected: new Set([id]), anchor: id };
  if (mode === 'toggle') {
    const selected = new Set(current);
    if (selected.has(id)) selected.delete(id);
    else selected.add(id);
    return { selected, anchor: id };
  }
  const from = anchor ? order.indexOf(anchor) : -1;
  const to = order.indexOf(id);
  if (from < 0 || to < 0) return { selected: new Set([...current, id]), anchor: anchor ?? id };
  const [lo, hi] = from < to ? [from, to] : [to, from];
  return { selected: new Set([...current, ...order.slice(lo, hi + 1)]), anchor };
}

/** As pastas da raiz até a pasta (o caminho do alto da página). */
export const folderPath = (folders: readonly DeskFolder[], folderId: string): DeskFolder[] => folderChain(folders, folderId);

/** As pastas filhas (null: as da raiz), em ordem de nome. */
export const childFolders = (folders: readonly DeskFolder[], parentId: string | null): DeskFolder[] =>
  folders.filter((folder) => folder.parentId === parentId).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

/** O lugar ainda existe no resumo (a pasta pode ter sido apagada noutra aba; a mesa, saído da lista). */
export function locationExists(at: CloudLocation, summary: DeskCloudSummary | null): boolean {
  if (!summary) return true;
  if (at.view === 'folder') return summary.folders.some((folder) => folder.id === at.folderId);
  if (at.view === 'room') return summary.rooms.some((room) => room.id === at.roomId);
  return true;
}

/** As ações de "Usar na mesa" (docs/spec/desk-cloud.md): só o que o RPG Desk já faz. */
export type UseAction = 'scene' | 'background' | 'token' | 'play' | 'playlist' | 'chat';

const USE_BY_KIND: Readonly<Record<FileKind, readonly UseAction[]>> = {
  map: ['scene', 'background', 'chat'],
  token: ['token', 'chat'],
  audio: ['play', 'playlist', 'chat'],
  doc: ['chat'],
  other: ['chat'],
};

/** As que mexem na cena ou na música são só dos mestres. */
export const GM_ACTIONS: ReadonlySet<UseAction> = new Set(['scene', 'background', 'token', 'play', 'playlist']);

/** As ações de "Usar na mesa" do arquivo, para quem é (`gm`) ou não é mestre da mesa. Imagem não toca; áudio não vira cena. */
export function actionsFor(file: Pick<DeskFile, 'kind' | 'mime'>, gm: boolean): UseAction[] {
  const image = file.mime.startsWith('image/');
  const audio = file.mime.startsWith('audio/') || file.kind === 'audio';
  return USE_BY_KIND[file.kind].filter((action) => {
    if (!gm && GM_ACTIONS.has(action)) return false;
    if ((action === 'scene' || action === 'background' || action === 'token') && !image) return false;
    if ((action === 'play' || action === 'playlist') && !audio) return false;
    return true;
  });
}

/** O total em bytes. */
export const totalBytes = (files: readonly { size: number }[]): number => files.reduce((sum, file) => sum + file.size, 0);

type T = (key: string, params?: Record<string, string | number>) => string;

/** O texto com a contagem: a chave `.one` para um, a outra para os demais. */
export const plural = (t: T, key: string, n: number, params: Record<string, string | number> = {}): string => t(n === 1 ? `${key}.one` : key, { n, ...params });

/** A data curta da lista: "Hoje, 14:32", "Ontem", "21 de out." (com o ano, se não for o deste ano). */
export function dateText(t: T, at: number, language: string | undefined, now = Date.now()): string {
  const day = new Date(at);
  const today = new Date(now);
  const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(day, today)) return t('cloud.today', { time: day.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' }) });
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  if (sameDay(day, yesterday)) return t('cloud.yesterday');
  return day.toLocaleDateString(language, { day: '2-digit', month: 'short', ...(day.getFullYear() === today.getFullYear() ? {} : { year: 'numeric' }) });
}

/**
 * As mesas em que dá para usar o arquivo: as ligadas a ele primeiro, depois as que a pessoa mestra; só
 * as que têm alguma ação para o tipo dele. `nameOf` dá o nome de uma mesa ligada que não está no resumo.
 */
export function roomTargets(file: Pick<DeskFile, 'kind' | 'mime' | 'rooms'>, rooms: readonly DeskRoom[], nameOf: (roomId: string) => string): DeskRoom[] {
  const linked = file.rooms.map((id): DeskRoom => rooms.find((room) => room.id === id) ?? { id, name: nameOf(id), files: 0, members: 0, gm: false });
  return [...linked, ...rooms.filter((room) => room.gm && !file.rooms.includes(room.id))].filter((room) => actionsFor(file, room.gm).length > 0);
}
